import React, { useState, useEffect } from "react";
import { Card, Form, Row, Col, Button, Table } from "react-bootstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { handle401 } from "../../../utils/handleAuthError";

const BASE_URL = "https://dev.sutmmg.codydeasoftech.in/api";

const OverviewList = () => {
  const [loading, setLoading] = useState(false);
  const [overviewList, setOverviewList] = useState([]);

  const [schools, setSchools] = useState([]);
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sectionlList, setSectionList] = useState([]);

  const [filters, setFilters] = useState({
    school_id: "",
    item_id: "",
    class_id: "",
    section_id: "",
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const theadStyle = {
    padding: "5px",
    fontWeight: "600",
    letterSpacing: "0.5px",
    backgroundColor: "#edf7fe",
    borderBottom: "1px solid black",
    borderTop: "1px solid black",
    textAlign: "center",
  };

  /* ---------------- FETCH DROPDOWNS ---------------- */

  // =====================
  // FETCH SCHOOLS
  // =====================
  const fetchSchools = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${BASE_URL}/schoolList`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setSchools(res.data?.data || []);
    } catch (error) {
      if (handle401(error)) return;

      console.error("Fetch schools error", error);
    }
  };

  const fetchSectionList = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BASE_URL}/sectionList`,

        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.status === true) {
        setSectionList(response.data.data.sectionList || []);
      } else {
        console.log("error sectionlist");
      }
    } catch (err) {
      console.error("Student count error", err);
      if (handle401(err)) return;
    }
  };

  // =====================
  // FETCH ITEMS
  // =====================
  const fetchItems = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.post(
        `${BASE_URL}/itemsList`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const filteredItems =
        res.data?.data?.itemsList?.filter(
          (item) => Number(item.status) !== 0,
        ) || [];

      setItems(filteredItems);
    } catch (error) {
      if (handle401(error)) return;
      console.error("Fetch items error", error);
    }
  };

  // =====================
  // FETCH CLASSES
  // =====================
  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${BASE_URL}/classList`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setClasses(res.data?.data.class_list || []);
    } catch (error) {
      if (handle401(error)) return;
      console.error("Fetch classes error", error);
    }
  };

  /* ---------------- FETCH OVERVIEW LIST ---------------- */

  const fetchOverviewList = async (
    pageNo = 1,
    filterValues = filters,
    perPageOverride = perPage,
  ) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const payload = {
        page: pageNo,
        per_page: perPageOverride,
        school_id: filterValues.school_id || "",
        item_id: filterValues.item_id || "",
        class_id: filterValues.class_id || "",
        section_id: filterValues.section_id || "",
      };

      const res = await axios.post(
        `${BASE_URL}/measurementsOverviewList`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setOverviewList(res.data?.data || []);
      setPage(res.data?.page || 1);
      setTotalRecords(res.data?.total_records || 0);
      setTotalPages(
        Math.ceil(
          (res.data?.total_records || 0) /
            (res.data?.per_page || perPageOverride),
        ),
      );
    } catch (err) {
      if (handle401(err)) return;
      Swal.fire("Alert", "Failed to load overview list", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- PAGINATION ---------------- */

  const getPageNumbers = () => {
    const pages = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + 4);

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  /* ---------------- EFFECTS ---------------- */

  useEffect(() => {
    fetchSchools();
    fetchItems();
    fetchClasses();
    fetchSectionList();
    fetchOverviewList(1);
  }, []);

  useEffect(() => {
    fetchOverviewList(1);
  }, [perPage]);

  return (
    <div className="container mt-4" style={{ maxWidth: 1550 }}>
      {/* 🔷 HEADER */}
      <Card
        className="mb-4 border-0"
        style={{
          background: "linear-gradient(135deg, #E3F2FD, #ffffff)",
          borderRadius: "14px",
          boxShadow: "0 8px 25px rgba(21,101,193,0.15)",
        }}
      >
        <div
          className="px-4 py-3 text-white"
          style={{ background: "linear-gradient(90deg, #0D47A1, #1565C1)" }}
        >
          <h5 className="mb-0 fw-bold">
            <i className="fa-solid fa-filter me-2"></i>
            Measurements Overview
          </h5>
        </div>

        <Card.Body>
          <Row>
            {/* SCHOOL */}
            <Col md={3}>
              <Form.Label className="fw-semibold text-dark">School</Form.Label>
              <Form.Select
                value={filters.school_id}
                onChange={(e) =>
                  setFilters({ ...filters, school_id: e.target.value })
                }
                style={{
                  borderRadius: "10px",
                  border: "2px solid #1565C1",
                }}
              >
                <option value="">All Schools</option>
                {schools.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* ITEM */}
            <Col md={3}>
              <Form.Label className="fw-semibold text-dark">Item</Form.Label>
              <Form.Select
                value={filters.item_id}
                onChange={(e) =>
                  setFilters({ ...filters, item_id: e.target.value })
                }
                style={{
                  borderRadius: "10px",
                  border: "2px solid #1565C1",
                }}
              >
                <option value="">All Items</option>
                {items.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.item_name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* CLASS */}
            <Col md={3}>
              <Form.Label className="fw-semibold text-dark">Class</Form.Label>
              <Form.Select
                value={filters.class_id}
                onChange={(e) =>
                  setFilters({ ...filters, class_id: e.target.value })
                }
                style={{
                  borderRadius: "10px",
                  border: "2px solid #1565C1",
                }}
              >
                <option value="">All Classes</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Section */}
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-black">
                  Section
                </Form.Label>

                <Form.Select
                  value={filters.section_id}
                  onChange={(e) =>
                    setFilters({ ...filters, section_id: e.target.value })
                  }
                  style={{
                    borderRadius: "10px",
                    border: "2px solid #1565C1",
                  }}
                >
                  <option value="">Select Section</option>

                  {sectionlList.map((sec) => (
                    <option key={sec.id} value={sec.id}>
                      {sec.section_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* PER PAGE */}
            <Col md={2}>
              <Form.Label className="fw-semibold text-dark">
                Record Showing
              </Form.Label>
              <Form.Select
                value={perPage}
                style={{
                  borderRadius: "10px",
                  border: "2px solid #1565C1",
                }}
                onChange={(e) => setPerPage(Number(e.target.value))}
              >
                <option value={10}>10 Per Page</option>
                <option value={25}>25 Per Page</option>
                <option value={50}>50 Per Page</option>
                <option value={100}>100 Per Page</option>
              </Form.Select>
            </Col>

            {/* BUTTONS */}
            <Col md={4} className="d-flex gap-3 mt-4">
              {/* SEARCH */}
              <Button
                className="w-50 fw-semibold text-white"
                style={{
                  background: "#1565C1",
                  border: "none",
                  borderRadius: "10px",
                  transition: "0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#0D47A1")}
                onMouseLeave={(e) => (e.target.style.background = "#1565C1")}
                onClick={() => fetchOverviewList(1, filters)}
              >
                <i className="fa-solid fa-magnifying-glass me-2"></i> Search
              </Button>

              {/* RESET */}
              <Button
                className="w-50 fw-semibold text-white"
                style={{
                  background: "#E53935",
                  border: "none",
                  borderRadius: "10px",
                  transition: "0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#D32F2F")}
                onMouseLeave={(e) => (e.target.style.background = "#E53935")}
                onClick={() => {
                  const resetFilters = {
                    school_id: "",
                    item_id: "",
                    class_id: "",
                    section_id: "",
                  };
                  setFilters(resetFilters);
                  setPage(1);
                  setPerPage(10);
                  fetchOverviewList(1, resetFilters); // Pass the new filters explicitly
                }}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i> Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* 📄 TABLE */}
      <Card className="border-0 shadow">
        <Table bordered hover responsive striped>
          <thead>
            <tr>
              <th style={theadStyle}>SL</th>
              <th style={theadStyle}>Item</th>
              <th style={theadStyle}>Size</th>
              <th style={theadStyle}>Total Quantity</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : overviewList.length ? (
              overviewList.map((row, i) => (
                <tr key={row.id}>
                  <td className="text-center">
                    {(page - 1) * perPage + i + 1}
                  </td>
                  <td className="text-center">{row.item_name}</td>
                  <td className="text-center">{row.size}</td>
                  <td className="text-center">{row.total_quantity} Pcs</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <nav>
            <ul className="pagination shadow-sm">
              {/* FIRST */}
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => fetchOverviewList(1)}
                  disabled={page === 1}
                >
                  «
                </button>
              </li>

              {/* PREVIOUS */}
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => fetchOverviewList(page - 1)}
                  disabled={page === 1}
                >
                  ‹
                </button>
              </li>

              {/* PAGE NUMBERS */}
              {getPageNumbers().map((p) => (
                <li
                  key={p}
                  className={`page-item ${page === p ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchOverviewList(p)}
                  >
                    {p}
                  </button>
                </li>
              ))}

              {/* NEXT */}
              <li
                className={`page-item ${page === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => fetchOverviewList(page + 1)}
                  disabled={page === totalPages}
                >
                  ›
                </button>
              </li>

              {/* LAST */}
              <li
                className={`page-item ${page === totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => fetchOverviewList(totalPages)}
                  disabled={page === totalPages}
                >
                  »
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

export default OverviewList;
